import * as React from "react";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import LogOutBlock from "./LogOutBlock";
import { AuthContext } from "../../../context/auth-provider";
import { DataContext } from "../../../context/data-provider";
import WeekChooserInNav from "../../elements/WeekChooserInNav";
import locations from "../../../assets/data/locations";
import { ROLES } from "../../../assets/data/roles";
import { MenuItems } from "../../../interface/menuItemsInterface";
import adminSideBarItems from "../../../assets/data/adminSideBarItems";
import userSideBarItems from "../../../assets/data/userSideBarItems";

const drawerWidth = 240;
const openedMixin = (theme: Theme): CSSObject => ({
    width: drawerWidth,
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
});
const closedMixin = (theme: Theme): CSSObject => ({
    transition: theme.transitions.create("width", {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: `calc(${theme.spacing(7)} + 1px)`,
    [theme.breakpoints.down("sm")]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
    [theme.breakpoints.up("sm")]: {
        width: `calc(${theme.spacing(8)} + 1px)`,
    },
});
const DrawerHeader = styled("div")(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}));
interface AppBarProps extends MuiAppBarProps {
    open?: boolean;
}
const AppBar = styled(MuiAppBar, {
    shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
    zIndex: theme.zIndex.drawer + 1,
    width: `calc(100% - ${drawerWidth - 175}px)`,
    transition: theme.transitions.create(["width", "margin"], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    ...(open && {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(["width", "margin"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
}));
const Drawer: any = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    ...(open && {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
    }),
}));

export default function SideBar() {
    const [open, setOpen] = React.useState(false);
    const [currentScreen, setCurrentScreen] = React.useState("");
    const [openSubtems, setOpenSubtems] = React.useState({
        isHolidayopen: false,
        isSkillsReviewOpen: false,
    });
    const [selectedWeek, setSelectedWeek] = React.useState<number>();
    const [firstDayOfWeek, setFirstDayOfWeek] = React.useState<string>();
    const [sidebarItems, setSidebarItems] = React.useState<MenuItems[]>([]);

    const { setSession } = React.useContext(AuthContext);
    const { currentUser } = React.useContext(DataContext);

    React.useEffect(() => {
        const fetchData = async () => {
            if (currentUser.role_id === ROLES.USER) {
                setSidebarItems(userSideBarItems);
            } else if (
                currentUser.role_id === ROLES.ADMIN ||
                currentUser.role_id === ROLES.SUPER_ADMIN
            ) {
                setSidebarItems(adminSideBarItems);
            }
        };

        fetchData();
    }, [currentUser]);

    const location = useLocation();

    React.useEffect(() => {
        const foundLinkName = locations.find(
            (item) => item.link === location.pathname
        );

        if (foundLinkName) {
            setCurrentScreen(foundLinkName!.name);
        }
    }, []);

    return (
        <Box sx={{ display: "flex" }}>
            <CssBaseline />
            <AppBar
                position="fixed"
                open={open}
                sx={{
                    "&.MuiAppBar-colorPrimary": {
                        color: "primary.main",
                        backgroundColor: "background.default",
                    },
                }}
            >
                <Toolbar
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography
                        variant="h6"
                        noWrap
                        component="div"
                        fontWeight={600}
                        className="location_name"
                    >
                        {currentScreen}
                    </Typography>
                    {currentScreen === "Dashboard" && (
                        <WeekChooserInNav
                            setSelectedWeek={setSelectedWeek}
                            setFirstDayOfWeek={setFirstDayOfWeek}
                        />
                    )}
                </Toolbar>
            </AppBar>
            <Drawer
                className={open ? "openedMenu" : "closedMenu"}
                variant="permanent"
                open={open}
                sx={{
                    "& .MuiDrawer-paper": {
                        backgroundColor: "custom.darkTeal",
                    },
                }}
            >
                <DrawerHeader>
                    <IconButton
                        onClick={() => setOpen(!open)}
                        title={open ? "Close Menu" : "Open Menu"}
                    >
                        <MenuIcon
                            sx={{
                                color: "custom.sidebarIconActive",
                                ml: "4px",
                            }}
                        />
                    </IconButton>
                    {open && (
                        <>
                            <img
                                src="/logoGrey.svg"
                                width={17}
                                height={17}
                                style={{ marginLeft: "1rem" }}
                            />
                            <Typography
                                component="h6"
                                variant="h6"
                                fontWeight="bold"
                                sx={{
                                    color: "custom.sidebarIconActive",
                                    ml: "2px",
                                    mt: "2px",
                                }}
                            >
                                <span style={{ marginRight: "2px" }}>-</span>
                                Gate
                            </Typography>
                        </>
                    )}
                </DrawerHeader>
                <Divider />
                <List>
                    {sidebarItems.map((item, id) => (
                        <div key={id}>
                            <NavLink
                                to={item.link}
                                style={{ textDecoration: "none" }}
                                onClick={() => {
                                    if (item.name === "Logout") {
                                        setSession(null);
                                        localStorage.removeItem("token");
                                    }
                                    [setCurrentScreen(item.name)];
                                }}
                            >
                                <ListItem
                                    disablePadding
                                    sx={{
                                        display:
                                            open && item.name === "Logout"
                                                ? "none"
                                                : "block",
                                    }}
                                >
                                    <ListItemButton
                                        sx={{
                                            minHeight: 48,
                                            justifyContent: open
                                                ? "initial"
                                                : "center",
                                            px: 2.5,
                                        }}
                                    >
                                        <ListItemIcon
                                            className="theSidebarIcon"
                                            sx={{
                                                minWidth: 0,
                                                mr: open ? 3 : "auto",
                                                justifyContent: "center",
                                            }}
                                            title={item.name}
                                        >
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            className="theSidebarText"
                                            primary={item.name}
                                            sx={{
                                                opacity: open ? 1 : 0,
                                            }}
                                        />
                                        {item.subitems && open ? (
                                            <>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        padding: "3px",
                                                    }}
                                                    onClick={() => {
                                                        if (
                                                            item.name ===
                                                            "Holidays"
                                                        ) {
                                                            setOpenSubtems({
                                                                ...openSubtems,
                                                                isHolidayopen:
                                                                    !openSubtems.isHolidayopen,
                                                            });
                                                        } else if (
                                                            item.name ===
                                                            "Skills review"
                                                        ) {
                                                            setOpenSubtems({
                                                                ...openSubtems,
                                                                isSkillsReviewOpen:
                                                                    !openSubtems.isSkillsReviewOpen,
                                                            });
                                                        }
                                                    }}
                                                >
                                                    {openSubtems[
                                                        item.name === "Holidays"
                                                            ? "isHolidayopen"
                                                            : "isSkillsReviewOpen"
                                                    ] ? (
                                                        <KeyboardArrowUpIcon />
                                                    ) : (
                                                        <KeyboardArrowDownIcon />
                                                    )}
                                                </div>
                                            </>
                                        ) : null}
                                    </ListItemButton>
                                </ListItem>
                            </NavLink>
                            {openSubtems[
                                item.name === "Holidays"
                                    ? "isHolidayopen"
                                    : "isSkillsReviewOpen"
                            ] &&
                                open && (
                                    <List sx={{ p: 0 }}>
                                        {item.subitems?.map((subitem, id) => (
                                            <NavLink
                                                key={id}
                                                to={subitem.link}
                                                style={{
                                                    textDecoration: "none",
                                                }}
                                                onClick={() =>
                                                    setCurrentScreen(
                                                        subitem.name
                                                    )
                                                }
                                            >
                                                <ListItem
                                                    disablePadding
                                                    sx={{ display: "block" }}
                                                >
                                                    <ListItemButton
                                                        sx={{
                                                            minHeight: 48,
                                                            justifyContent: open
                                                                ? "initial"
                                                                : "center",
                                                            pl: 4.5,
                                                        }}
                                                    >
                                                        <ListItemIcon
                                                            sx={{
                                                                minWidth: 0,
                                                                mr: open
                                                                    ? 0
                                                                    : "auto",
                                                                justifyContent:
                                                                    "center",
                                                            }}
                                                        >
                                                            {subitem.icon}{" "}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            className="theSidebarSubItemText"
                                                            primary={
                                                                subitem.name
                                                            }
                                                            sx={{
                                                                opacity: open
                                                                    ? 1
                                                                    : 0,
                                                                pl: 3,
                                                            }}
                                                        />
                                                    </ListItemButton>
                                                </ListItem>
                                            </NavLink>
                                        ))}
                                    </List>
                                )}
                        </div>
                    ))}
                </List>
                <Divider />
                {open && (
                    <Box
                        sx={{
                            display: "flex",
                            width: "100%",
                            height: "100%",
                            justifyContent: "center",
                            alignItems: "flex-end",
                        }}
                    >
                        <LogOutBlock setSession={setSession} />
                    </Box>
                )}
            </Drawer>
            <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                <DrawerHeader />
                <Outlet context={[selectedWeek, firstDayOfWeek]} />
            </Box>
        </Box>
    );
}
